/**
 * PriceIntelligence — the PDP "Fridge.com Price Intelligence" module
 * (conversion/trust lane; the heading carries the brand first — owner
 * directive FRIDGE-347, 08/17/2026). A compact card for the money zone:
 * current price vs the tracked
 * average vs the lowest we've recorded, a one-line honest verdict, and — when
 * a real time-series exists — a small sparkline.
 *
 * All gating lives in derivePriceIntelligence (./derive). Since the owner's
 * 08/27/2026 ruling (FRIDGE-583: price tracking visible on ALL products,
 * always) the view-model is null ONLY when the product has no live price —
 * on every priced page the module renders, degrading honestly by rung:
 * full verdict+chart → facts-without-claim → "tracking started". Every
 * number traces back to OUR keepa_price_snapshots stats and the live
 * displayed offer price; nothing is fabricated at any rung.
 */

import { derivePriceIntelligence, type SparkPoint } from './derive'
import { PriceSparkline } from './PriceSparkline'
import type { PriceHistoryStats } from '@/components/deal-verdict/compute'

interface PriceIntelligenceProps {
  currentCents: number | null | undefined
  stats: PriceHistoryStats | null | undefined
  series?: SparkPoint[] | null
}

function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`
}

export function PriceIntelligence({ currentCents, stats, series }: PriceIntelligenceProps) {
  const vm = derivePriceIntelligence({ currentCents, stats, series })
  if (!vm) return null

  const pill = vm.positive
    ? 'text-green-800 bg-green-50 border-green-200'
    : 'text-gray-700 bg-gray-50 border-gray-200'

  return (
    <div
      data-testid="price-intelligence"
      className="mb-4 rounded-lg border border-gray-200 bg-white p-4"
    >
      <div className="flex items-center gap-1.5 mb-3">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 17l6-6 4 4 8-8" stroke="#0469f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 7v5h-5" stroke="#0469f0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {/* FRIDGE-1504 L1504-9: an h3, the level under the money zone's
            "Current Offers" h2 (an h4 skipped a level on PDP, part and
            filter pages). Size and weight live on the classes. */}
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-900">
          Fridge.com Price Intelligence
        </h3>
      </div>

      {/* Three honest stats */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-gray-50 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Current</div>
          <div className="text-sm font-black text-gray-900" data-testid="pi-current">{dollars(vm.currentCents)}</div>
        </div>
        <div className="rounded-md bg-gray-50 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{vm.averageLabel}</div>
          <div className="text-sm font-black text-gray-900" data-testid="pi-average">
            {vm.averageCents != null ? dollars(vm.averageCents) : '—'}
          </div>
        </div>
        <div className="rounded-md bg-gray-50 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Lowest tracked</div>
          <div className="text-sm font-black text-gray-900" data-testid="pi-lowest">
            {vm.lowestCents != null ? dollars(vm.lowestCents) : '—'}
          </div>
          {vm.lowestMonthYear && (
            <div className="text-[10px] text-gray-500 leading-tight">{vm.lowestMonthYear}</div>
          )}
        </div>
      </div>

      {/* Verdict line — only when the 14-day claim floor is met (the module
          itself no longer hides below it; the CLAIM does). */}
      {vm.headline && (
        <div className="mt-3">
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1 ${pill}`}
            data-testid="pi-verdict"
          >
            {vm.positive && <span aria-hidden="true">↓</span>}
            <span>{vm.headline}</span>
          </span>
        </div>
      )}

      {/*
        The honest ladder (FRIDGE-282). The two populated rungs draw a line —
        a moving series, and since FRIDGE-1151 a price that held, drawn flat
        with a sentence saying so. The thin and empty rungs state what we
        actually hold rather than dressing it as a trend. Measured 08/16/2026: of the
        4,835 active products that render a page, 104 (2.2%) reach the chart.
      */}
      {(vm.rung.kind === 'chart' || vm.rung.kind === 'no-movement') && vm.sparkline && (
        <div className="mt-3" data-testid="pi-sparkline">
          <PriceSparkline
            points={vm.sparkline}
            currentCents={vm.currentCents}
            lowestCents={vm.lowestCents}
            averageCents={vm.averageCents}
          />
          <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#16a34a]" aria-hidden="true" /> Lowest
            </span>
            <span>Tracked since {vm.trackedSinceMonthYear}</span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#ff9951]" aria-hidden="true" /> Latest reading
            </span>
          </div>
        </div>
      )}

      {vm.rung.kind === 'no-movement' && (
        <p
          className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs leading-snug text-gray-700"
          data-testid="pi-no-movement"
        >
          <span className="font-semibold text-gray-900">The tracked price has not moved.</span>{' '}
          Every one of the {vm.rung.points.length.toLocaleString('en-US')} readings we
          have taken since {vm.trackedSinceMonthYear} came back at{' '}
          {dollars(vm.rung.priceCents)}, so the line above is flat. Prices between
          checks are unknown.
          {/* FRIDGE-1151: "Current" is today's best retailer offer, the line is
              the tracked readings. When they differ the card must say so rather
              than state "has not moved" beside a different number. */}
          {dollars(vm.currentCents) !== dollars(vm.rung.priceCents) && (
            <>
              {' '}The current price above, {dollars(vm.currentCents)}, is today&apos;s best
              offer and is not one of these readings.
            </>
          )}
        </p>
      )}

      {vm.rung.kind === 'too-thin' && vm.rung.points.length > 0 && (
        <p
          className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs leading-snug text-gray-700"
          data-testid="pi-too-thin"
        >
          <span className="font-semibold text-gray-900">Not enough history to chart yet.</span>{' '}
          We have {vm.rung.points.length.toLocaleString('en-US')}{' '}
          {vm.rung.points.length === 1 ? 'reading' : 'readings'} since{' '}
          {vm.trackedSinceMonthYear} — too few to show a shape without guessing at
          the gaps, so we are not drawing one.
        </p>
      )}

      {vm.rung.kind === 'tracking-started' && (
        <p
          className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs leading-snug text-gray-700"
          data-testid="pi-tracking-started"
        >
          <span className="font-semibold text-gray-900">Verified price history is unavailable.</span>{' '}
          Current offers are shown above. A current offer alone does not establish
          a price history, so there is no historical comparison to show.
        </p>
      )}

      {/* Standing honesty disclosure. Retailer-neutral by owner directive
          (FRIDGE-350, 08/17/2026): the sentence names no store. */}
      <p className="mt-3 text-[11px] leading-snug text-gray-500">
        {vm.sampleDays > 0 && vm.trackedSinceMonthYear ? (
          <>Based on {vm.sampleDays.toLocaleString('en-US')} prices we&rsquo;ve tracked
          since {vm.trackedSinceMonthYear}. </>
        ) : (
          <>Verified historical readings are unavailable for this product. </>
        )}
        Some retailers only reveal their lowest price in
        cart — we never guess those; figures use displayed prices only.
      </p>
    </div>
  )
}
